import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  type OutlookStatusAccount,
  useMicrosoftOAuthConnect,
  useOutlookDisconnect,
  useOutlookStatus,
} from '@/hooks/integrations/microsoft';
import { useDeletionStatus } from '@/hooks/integrations/useDeletionStatus';
import { useDisconnectWithDeletion } from '@/hooks/integrations/useDisconnectWithDeletion';
import { useIntegrationVisibility } from '@/hooks/integrations/useIntegrationVisibility';
import { usePermissions } from '@/hooks/usePermissions';
import { useUserProfileData } from '@/hooks/useUserProfileData';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { AlertFloating, ConfirmationDialog } from '@/lib/base/overlays';
import { getMicrosoftClientId } from '@/lib/config';
import { isTransientError } from '@/lib/errors';

import UnifiedImportStatus from './imports/UnifiedImportStatus';
import AccountDntEditor from './integrations/AccountDntEditor';
import AccountSignatureEditor from './integrations/AccountSignatureEditor';
import {
  DeleteSyncedDataChecklist,
  type DeleteSyncedDataChecklistRow,
} from './integrations/DeleteSyncedDataChecklist';
import {
  ConnectedBadge,
  IntegrationAccountCard,
  type IntegrationBenefit,
  IntegrationBenefitsCard,
  IntegrationProviderHeader,
  IntegrationVisibilityToggle,
} from './integrations/IntegrationCards';
import IntegrationDeletionStatus from './integrations/IntegrationDeletionStatus';
import OutlookSyncNowButton from './integrations/OutlookSyncNowButton';
import {
  MicrosoftBrandMark,
  ProviderLogoTile,
} from './integrations/ProviderBrandMarks';
import {
  type DataVisibility,
  VisibilityChoiceModal,
} from './integrations/VisibilityChoiceModal';

const IS_MICROSOFT_CONFIGURED = Boolean(getMicrosoftClientId());

const OUTLOOK_LOGO_SRC = '/assets/figma/outlook-icon.svg';

const OUTLOOK_BENEFITS: IntegrationBenefit[] = [
  // Outcome-framed and length-matched — see the note in GMAIL_BENEFITS.
  {
    label: 'Outlook Mail:',
    text: 'Replies drafted with the full thread in context',
  },
  {
    label: 'Outlook Calendar:',
    text: 'Times proposed against your real availability',
  },
  { label: 'Your CRM:', text: 'Email and meetings logged to the right record' },
];

const CALENDAR_VISIBILITY_TITLE =
  'Make calendar events public to team by default';
const CALENDAR_VISIBILITY_DESC =
  'When enabled, calendar events with default visibility will be visible to all team members. This will also apply to previously synced events.';
const MAIL_VISIBILITY_TITLE = 'Make emails public to team by default';
const MAIL_VISIBILITY_DESC =
  'When enabled, emails will be visible to all team members. This will also apply to previously synced emails.';

const OUTLOOK_DISCONNECT_DESCRIPTION =
  'This will disable Outlook Mail and Calendar capabilities for this account. You can reconnect any time.';

function OutlookLogo({ className }: { className?: string }) {
  return <img src={OUTLOOK_LOGO_SRC} alt='Outlook' className={className} />;
}

// The provider header is titled "Microsoft 365 / Outlook" — i.e. the whole
// Microsoft account, not just the mail app — so it uses the Microsoft mark from
// ProviderBrandMarks. The Outlook envelope above is still correct for the
// per-mailbox rows below.

function buildDeleteChecklistRows(
  account: OutlookStatusAccount
): DeleteSyncedDataChecklistRow[] {
  const rows: DeleteSyncedDataChecklistRow[] = [];
  if (account.outlook_mail_entity_id) {
    rows.push({
      entityType: 'ahoy_email_thread',
      label: 'Delete synced emails',
      integrationEntityId: account.outlook_mail_entity_id,
      isChecked: false,
    });
  }
  if (account.outlook_calendar_entity_id) {
    rows.push({
      entityType: 'ahoy_meeting',
      label: 'Delete synced calendar events',
      integrationEntityId: account.outlook_calendar_entity_id,
      isChecked: false,
    });
  }
  return rows;
}

interface OutlookAccountCardProps {
  account: OutlookStatusAccount;
  orgId: string;
  userId: number | undefined;
  canModifyIntegration: boolean;
  onDisconnect: (account: OutlookStatusAccount) => void;
  isDisconnecting: boolean;
  onReconnect: () => void;
  isReconnecting: boolean;
  onVisibilityToggle: (entityId: string, currentIsOrg: boolean) => void;
  updatingEntityId: string | null;
  onOpenDeleteSyncedData: (account: OutlookStatusAccount) => void;
}

function OutlookAccountCard({
  account,
  orgId,
  userId,
  canModifyIntegration,
  onDisconnect,
  isDisconnecting,
  onReconnect,
  isReconnecting,
  onVisibilityToggle,
  updatingEntityId,
  onOpenDeleteSyncedData,
}: OutlookAccountCardProps) {
  const handleDisconnect = useCallback(() => {
    onDisconnect(account);
  }, [onDisconnect, account]);

  // The Email settings section links here with #outlook-signature — scroll the
  // primary account's signature editor into view when that hash is present.
  // Hash handling must be manual: the browser's native anchor scroll fires
  // before this card exists (the account list renders after the status fetch).
  const signatureBlockRef = useRef<HTMLDivElement | null>(null);
  const { hash } = useLocation();
  const isSignatureLinkTarget = account.is_primary;
  useEffect(() => {
    if (
      hash === '#outlook-signature' &&
      isSignatureLinkTarget &&
      signatureBlockRef.current
    ) {
      signatureBlockRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [hash, isSignatureLinkTarget]);

  const logo = <OutlookLogo className='w-8 h-8 shrink-0' />;

  // Orphaned: the integration exists but its SocialAccount was removed.
  if (account.social_account_id === null) {
    return (
      <OrphanedOutlookCard
        account={account}
        orgId={orgId}
        canModifyIntegration={canModifyIntegration}
        onReconnect={onReconnect}
        isReconnecting={isReconnecting}
        onOpenDeleteSyncedData={onOpenDeleteSyncedData}
      />
    );
  }

  if (account.needs_reconnect) {
    return (
      <IntegrationAccountCard
        logo={logo}
        email={account.email}
        subLabel='Authentication expired'
        badges={
          <Badge type='color' size='sm' color='warning'>
            Reconnect required
          </Badge>
        }
        actions={
          <Button
            size='sm'
            color='secondary'
            onClick={onReconnect}
            isDisabled={isReconnecting}
          >
            {isReconnecting ? 'Reconnecting…' : 'Reconnect Outlook'}
          </Button>
        }
      />
    );
  }

  return (
    <IntegrationAccountCard
      logo={logo}
      email={account.email}
      subLabel='Connected account'
      badges={<ConnectedBadge />}
      actions={
        <Button
          size='sm'
          color='secondary'
          onClick={handleDisconnect}
          isDisabled={isDisconnecting}
        >
          {isDisconnecting ? 'Disconnecting…' : 'Disconnect'}
        </Button>
      }
    >
      {orgId && (
        <div className='border-t border-border-secondary px-4 py-3 space-y-2'>
          {/* gcalEnabled=false: the "Sync Calendar" button posts to the
              Google-only /gcal-sync/ endpoint (404 for Outlook). Outlook
              calendar sync runs via the msgraph importer, so only the
              status display is reused here; the manual affordance is the
              Outlook-specific Sync now button below. */}
          <UnifiedImportStatus
            orgId={orgId}
            socialAccountId={account.social_account_id}
            gcalEnabled={false}
          />
          <OutlookSyncNowButton
            orgId={orgId}
            socialAccountId={account.social_account_id}
          />
        </div>
      )}
      {account.outlook_calendar_entity_id && (
        <IntegrationVisibilityToggle
          title={CALENDAR_VISIBILITY_TITLE}
          description={CALENDAR_VISIBILITY_DESC}
          entityId={account.outlook_calendar_entity_id}
          currentVisibility={
            account.outlook_calendar_default_data_visibility ?? undefined
          }
          isUpdating={updatingEntityId === account.outlook_calendar_entity_id}
          onToggle={onVisibilityToggle}
        />
      )}
      {account.outlook_mail_entity_id && (
        <IntegrationVisibilityToggle
          title={MAIL_VISIBILITY_TITLE}
          description={MAIL_VISIBILITY_DESC}
          entityId={account.outlook_mail_entity_id}
          currentVisibility={
            account.outlook_mail_default_data_visibility ?? undefined
          }
          isUpdating={updatingEntityId === account.outlook_mail_entity_id}
          onToggle={onVisibilityToggle}
        />
      )}
      {userId !== undefined && account.outlook_mail_entity_id && (
        <div className='border-t border-border-secondary px-4 py-3'>
          <AccountDntEditor
            orgId={orgId}
            account={account}
            entityId={account.outlook_mail_entity_id}
            userId={userId}
            canModify={canModifyIntegration}
          />
        </div>
      )}
      {userId !== undefined && account.outlook_mail_entity_id && (
        <div
          ref={signatureBlockRef}
          id={isSignatureLinkTarget ? 'outlook-signature' : undefined}
          className='border-t border-border-secondary px-4 py-3 scroll-mt-4'
        >
          <AccountSignatureEditor
            orgId={orgId}
            entityId={account.outlook_mail_entity_id}
            userId={userId}
            canModify={canModifyIntegration}
          />
        </div>
      )}
    </IntegrationAccountCard>
  );
}

interface OrphanedOutlookCardProps {
  account: OutlookStatusAccount;
  orgId: string;
  canModifyIntegration: boolean;
  onReconnect: () => void;
  isReconnecting: boolean;
  onOpenDeleteSyncedData: (account: OutlookStatusAccount) => void;
}

function OrphanedOutlookCard({
  account,
  orgId,
  canModifyIntegration,
  onReconnect,
  isReconnecting,
  onOpenDeleteSyncedData,
}: OrphanedOutlookCardProps) {
  const mailDeletion = useDeletionStatus({
    orgId,
    integrationEntityId: account.outlook_mail_entity_id || '',
  });
  const calendarDeletion = useDeletionStatus({
    orgId,
    integrationEntityId: account.outlook_calendar_entity_id || '',
  });

  const hasActiveDeletions =
    mailDeletion.hasActiveDeletions || calendarDeletion.hasActiveDeletions;

  const handleOpenDelete = useCallback(() => {
    onOpenDeleteSyncedData(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onOpenDeleteSyncedData, account.email]);

  const canDelete =
    canModifyIntegration &&
    (!!account.outlook_mail_entity_id || !!account.outlook_calendar_entity_id);

  const logo = <OutlookLogo className='w-8 h-8 shrink-0' />;

  return (
    <IntegrationAccountCard
      logo={logo}
      email={account.email}
      subLabel='Disconnected · No active sync'
      badges={
        <Badge type='color' size='sm' color='gray'>
          Disconnected
        </Badge>
      }
      actions={
        hasActiveDeletions ? (
          <div className='flex-1 ml-4 space-y-2'>
            {mailDeletion.hasActiveDeletions &&
              account.outlook_mail_entity_id && (
                <IntegrationDeletionStatus
                  orgId={orgId}
                  integrationEntityId={account.outlook_mail_entity_id}
                />
              )}
            {calendarDeletion.hasActiveDeletions &&
              account.outlook_calendar_entity_id && (
                <IntegrationDeletionStatus
                  orgId={orgId}
                  integrationEntityId={account.outlook_calendar_entity_id}
                />
              )}
          </div>
        ) : (
          <div className='flex items-center gap-2'>
            <Button
              size='sm'
              color='secondary'
              onClick={onReconnect}
              isDisabled={isReconnecting}
            >
              {isReconnecting ? 'Reconnecting…' : 'Reconnect Outlook'}
            </Button>
            {canDelete && (
              <Button size='sm' color='secondary' onClick={handleOpenDelete}>
                Delete synced data
              </Button>
            )}
          </div>
        )
      }
    />
  );
}

export default function OutlookIntegration() {
  const { currentOrg } = useOrg();
  const orgId =
    currentOrg?.id !== undefined ? String(currentOrg.id) : undefined;
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canModifyIntegration = hasPermission(
    'ahoy_external_integration',
    'ahoy_property_updated'
  );
  const { data: profileData } = useUserProfileData();
  const userId = profileData?.user?.id;
  const {
    connect,
    isConnecting,
    error: connectError,
  } = useMicrosoftOAuthConnect(orgId);
  const {
    data: status,
    isLoading,
    error: statusError,
  } = useOutlookStatus(orgId);
  const disconnect = useOutlookDisconnect(orgId);
  const disconnectError = disconnect.error;
  const isTransientStatusError =
    statusError != null && isTransientError(statusError);
  const hardStatusError = statusError != null && !isTransientStatusError;
  const { updateVisibility, currentlyUpdatingEntity } =
    useIntegrationVisibility(orgId);

  const [isVisibilityModalOpen, setIsVisibilityModalOpen] = useState(false);

  // Shared disconnect-with-deletion flow
  const disconnectFlow = useDisconnectWithDeletion<OutlookStatusAccount>({
    orgId,
    buildChecklistRows: buildDeleteChecklistRows,
    disconnect: async account => {
      if (account.social_account_id === null) {
        return;
      }
      await disconnect.mutateAsync({
        socialAccountId: account.social_account_id,
      });
    },
    baseDescription: OUTLOOK_DISCONNECT_DESCRIPTION,
    toastLabel: 'Outlook',
  });

  const handleConnect = useCallback(() => {
    setIsVisibilityModalOpen(true);
  }, []);

  const handleConfirmVisibility = useCallback(
    (visibility: DataVisibility) => {
      setIsVisibilityModalOpen(false);
      connect(visibility);
    },
    [connect]
  );

  const handleVisibilityToggle = useCallback(
    (entityId: string, currentIsOrg: boolean) => {
      updateVisibility(entityId, currentIsOrg ? 'private' : 'org').then(ok => {
        if (ok) {
          toast.success('Visibility updated');
          queryClient.invalidateQueries({
            queryKey: ['microsoft', 'outlook', 'status', orgId],
          });
        } else {
          toast.error("We couldn't update visibility. Try again.");
        }
      });
    },
    [updateVisibility, queryClient, orgId]
  );

  const hasAccounts = (status?.accounts.length ?? 0) > 0;

  return (
    <div
      className='space-y-4'
      data-testid='outlook-integration'
      data-component='settings-section'
      data-instance='microsoft-365'
    >
      {disconnectError && (
        <AlertFloating
          color='error'
          title="We couldn't disconnect this account"
          description={disconnectError.message ?? 'Please try again.'}
          onClose={() => disconnect.reset()}
        />
      )}

      <IntegrationProviderHeader
        logo={
          <ProviderLogoTile>
            <MicrosoftBrandMark />
          </ProviderLogoTile>
        }
        title='Microsoft 365 / Outlook'
        subtitle='For Outlook.com, Microsoft 365 and Exchange accounts'
        action={
          status?.connected ? (
            <ConnectedBadge />
          ) : (
            <Button
              size='sm'
              color='primary'
              isDisabled={!IS_MICROSOFT_CONFIGURED || isConnecting}
              onClick={handleConnect}
            >
              {isConnecting ? 'Connecting…' : 'Connect'}
            </Button>
          )
        }
      />

      {!IS_MICROSOFT_CONFIGURED && (
        <p className='text-sm text-tertiary'>
          Microsoft 365 integration is not configured
        </p>
      )}

      {isLoading && <p className='text-sm text-tertiary'>Loading…</p>}

      {!isLoading && hardStatusError && (
        <p className='text-sm text-error-700'>
          We couldn't load your Outlook connection status. Try again.
        </p>
      )}

      {!isLoading && isTransientStatusError && !hasAccounts && (
        <p className='text-sm text-tertiary'>Reconnecting…</p>
      )}

      {!isLoading && !hardStatusError && hasAccounts && (
        <div className='space-y-3'>
          {status?.accounts.map(account => (
            <OutlookAccountCard
              key={
                account.social_account_id ??
                account.outlook_mail_entity_id ??
                account.outlook_calendar_entity_id
              }
              account={account}
              orgId={orgId ?? ''}
              userId={userId}
              canModifyIntegration={canModifyIntegration}
              onDisconnect={disconnectFlow.handleDisconnectClick}
              isDisconnecting={disconnect.isPending}
              onReconnect={handleConnect}
              isReconnecting={isConnecting}
              onVisibilityToggle={handleVisibilityToggle}
              updatingEntityId={currentlyUpdatingEntity}
              onOpenDeleteSyncedData={disconnectFlow.handleOpenDeleteSyncedData}
            />
          ))}
        </div>
      )}

      {!isLoading &&
        !hardStatusError &&
        !hasAccounts &&
        IS_MICROSOFT_CONFIGURED && (
          <IntegrationBenefitsCard items={OUTLOOK_BENEFITS} />
        )}

      {connectError && <p className='text-sm text-error-700'>{connectError}</p>}

      <VisibilityChoiceModal
        isOpen={isVisibilityModalOpen}
        onOpenChange={setIsVisibilityModalOpen}
        onConfirm={handleConfirmVisibility}
        integrationLabel='Outlook'
        isConfirming={isConnecting}
      />

      {/* Disconnect confirmation dialog with data deletion checklist */}
      <ConfirmationDialog
        isOpen={disconnectFlow.isDisconnectDialogOpen}
        variant='warning'
        title='Disconnect Outlook account'
        description={disconnectFlow.checkedDescription}
        confirmLabel={disconnect.isPending ? 'Disconnecting…' : 'Disconnect'}
        showConfirmTextWhileLoading={true}
        keepCancelEnabledWhileLoading={true}
        isLoading={disconnect.isPending}
        onConfirm={disconnectFlow.handleConfirmDisconnect}
        onCancel={disconnectFlow.handleCancelDisconnect}
      >
        {canModifyIntegration && (
          <DeleteSyncedDataChecklist
            rows={disconnectFlow.checklistRows}
            onToggle={disconnectFlow.handleChecklistToggle}
            disabled={disconnect.isPending}
          />
        )}
      </ConfirmationDialog>

      {/* Delete synced data dialog (for disconnected/orphaned accounts) */}
      <ConfirmationDialog
        isOpen={disconnectFlow.disconnectedCardDeleteTarget !== null}
        title={`Delete synced data from ${disconnectFlow.disconnectedCardDeleteTarget?.email ?? ''}`}
        description='Choose what to delete. This permanently removes the selected data from Ahoy. This action cannot be undone.'
        variant='destructive'
        confirmLabel='Delete'
        isLoading={false}
        isConfirmDisabled={
          (disconnectFlow.disconnectedCardDeleteTarget?.rows.filter(
            r => r.isChecked
          ).length ?? 0) === 0
        }
        onConfirm={disconnectFlow.handleConfirmDeleteSyncedData}
        onCancel={disconnectFlow.handleCancelDeleteSyncedData}
        instance='delete-synced-data'
      >
        {canModifyIntegration &&
          disconnectFlow.disconnectedCardDeleteTarget && (
            <DeleteSyncedDataChecklist
              rows={disconnectFlow.disconnectedCardDeleteTarget.rows}
              onToggle={disconnectFlow.handleDeleteRowToggle}
              disabled={false}
            />
          )}
      </ConfirmationDialog>
    </div>
  );
}
